@if (isLoadingServices()) {
  <div
    class="mt-6 animate-pulse rounded border border-neutral-200 bg-gray-50 p-4"
    role="status"
    aria-label="Loading services"
  >
    <div class="mb-4 h-4 w-24 rounded bg-gray-200"></div>
    <div class="h-11 w-full rounded bg-gray-200"></div>
    @if (isDisconnectMode) {
      <div class="mt-4 mb-2 h-4 w-40 rounded bg-gray-200"></div>
      <div class="h-11 w-full rounded bg-gray-200"></div>
    }
    <p class="font-lato text-brand-muted mt-3 text-sm">
      Loading your services...
    </p>
  </div>
} @else {
  <div [formGroup]="formRef" [class.service-info-container]="!isDisconnectMode">
    @for (
      sli of formRef.controls.services['controls'];
      track $index;
      let i = $index
    ) {
      <div [formGroup]="sli" [ngClass]="serviceItemClass">
        @if (serviceOptions().length > 1) {
          <div [class.mt-4]="!isDisconnectMode">
            <div
              class="flex items-center justify-between"
              [class.mb-2]="!isDisconnectMode"
              [class.mb-4]="isDisconnectMode"
            >
              <label
                class="comcast-input-label"
                [attr.for]="'service-btn-' + i"
              >
                Service
                <span class="comcast-required-marker" aria-hidden="true"
                  >*</span
                >
              </label>
              @if (formRef.value.services.length > 1) {
                <button
                  type="button"
                  class="comcast-inline-link comcast-inline-link--danger"
                  [attr.aria-label]="'Remove service ' + (i + 1)"
                  (click)="removeServicesFormControl(i)"
                >
                  <mat-icon
                    class="!flex !h-4 !w-4 !items-center !justify-center !text-base"
                    aria-hidden="true"
                    >delete_outline</mat-icon
                  >
                  Remove
                </button>
              }
            </div>
            <div class="relative mt-2">
              <button
                [attr.id]="'service-btn-' + i"
                type="button"
                class="comcast-select-trigger"
                [class.comcast-select-trigger--open]="serviceDropdownOpen()[i]"
                [class.comcast-select-trigger--filled]="
                  !!getSelectedServiceLabel(i)
                "
                [attr.aria-expanded]="serviceDropdownOpen()[i]"
                aria-haspopup="listbox"
                [attr.aria-controls]="'service-listbox-' + i"
                aria-required="true"
                (click)="toggleServiceDropdown(i, $event)"
              >
                @if (getSelectedServiceLabel(i)) {
                  <div class="flex flex-col items-start text-left">
                    <span class="font-lato text-base leading-tight">
                      {{ getSelectedServiceLabel(i) }}
                    </span>
                    <span class="font-lato text-sm">
                      {{ getSelectedServiceSubLabel(i) }}
                    </span>
                  </div>
                } @else {
                  <span class="text-brand-muted">Please select a service</span>
                }
                <mat-icon class="comcast-select-icon" aria-hidden="true">
                  {{
                    serviceDropdownOpen()[i]
                      ? 'arrow_drop_up'
                      : 'arrow_drop_down'
                  }}
                </mat-icon>
              </button>

              @if (serviceDropdownOpen()[i]) {
                <!-- a11y listbox pattern: docs/accessibility/custom-listbox.md -->
                <div
                  [attr.id]="'service-listbox-' + i"
                  class="comcast-select-panel comcast-select-panel--open"
                  role="listbox"
                  aria-label="Services"
                  (click)="$event.stopPropagation()"
                >
                  <div class="comcast-select-search">
                    <mat-icon
                      class="comcast-select-search-icon"
                      aria-hidden="true"
                      >search</mat-icon
                    >
                    <input
                      type="text"
                      placeholder="Search by address or NIT..."
                      aria-label="Search services by address or NIT"
                      [value]="serviceSearchTerms()[i]"
                      (input)="onServiceSearchInput(i, $event)"
                      class="comcast-select-search-input"
                      appAutofocus
                    />
                  </div>
                  <div class="comcast-select-options">
                    @for (s of getFilteredServicesForIndex(i); track s.id) {
                      <div
                        class="comcast-select-option flex flex-col items-start"
                        role="option"
                        [attr.tabindex]="s.disabled ? -1 : 0"
                        [attr.aria-selected]="
                          formRef.value.services[i].serviceId === s.id
                        "
                        [attr.aria-disabled]="s.disabled"
                        [class.comcast-select-option--selected]="
                          formRef.value.services[i].serviceId === s.id
                        "
                        [class.pointer-events-none]="s.disabled"
                        [class.opacity-40]="s.disabled"
                        (click)="
                          !s.disabled && selectServiceOption(s.id, i, $event)
                        "
                        (keydown.enter)="
                          !s.disabled && selectServiceOption(s.id, i, $event)
                        "
                        (keydown.space)="
                          !s.disabled && selectServiceOption(s.id, i, $event)
                        "
                      >
                        <span class="leading-tight">{{ s.locationName }}</span>
                        <span class="text-sm">
                          {{ s.nit }} · {{ s.product }}
                          @if (!isDisconnectMode && s.bandwidth) {
                            <span> · {{ s.bandwidth }}</span>
                          }
                          @if (!isDisconnectMode && s.alias) {
                            <span> · {{ s.alias }}</span>
                          }
                        </span>
                      </div>
                    }
                    @if (!getFilteredServicesForIndex(i).length) {
                      <div
                        class="comcast-select-empty"
                        role="option"
                        aria-disabled="true"
                      >
                        No results found
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }

        @if (serviceOptions().length === 1) {
          <div [class.mt-4]="!isDisconnectMode">
            <p class="comcast-input-label">Service</p>
            <p class="font-lato mt-1 text-base">
              {{ serviceOptions()[0].nit }} · {{ serviceOptions()[0].product }}
            </p>
            <p class="font-lato text-sm">
              {{ serviceOptions()[0].locationName }}
            </p>
          </div>
        }

        @if (serviceOptions().length === 0) {
          <p
            class="font-lato text-brand-muted text-sm"
            [class.mt-4]="!isDisconnectMode"
          >
            {{ noActiveServicesMessage }}
          </p>
        }

        @if (isDisconnectMode && serviceOptions().length >= 1) {
          <div class="mt-4">
            <label class="comcast-input-label" [attr.for]="'date-btn-' + i">
              Desired disconnect date
              <span class="comcast-required-marker" aria-hidden="true">*</span>
            </label>
            <div class="relative mt-2">
              <button
                [attr.id]="'date-btn-' + i"
                type="button"
                class="comcast-select-trigger"
                [class.comcast-select-trigger--filled]="
                  !!sli.get('desiredDate').value
                "
                aria-haspopup="dialog"
                [attr.aria-describedby]="'date-hint-' + i"
                (click)="picker.open()"
              >
                <span [class.text-brand-muted]="!sli.get('desiredDate').value">
                  {{
                    (sli.get('desiredDate').value | date: 'longDate') ||
                      'Please select a date'
                  }}
                </span>
                <mat-icon class="comcast-select-icon" aria-hidden="true"
                  >calendar_month</mat-icon
                >
              </button>
              <input
                class="absolute h-0 w-0 opacity-0"
                formControlName="desiredDate"
                (dateChange)="canSubmit()"
                [matDatepickerFilter]="filterDates"
                [matDatepicker]="picker"
                aria-hidden="true"
                tabindex="-1"
                (keyup)="canSubmit()"
              />
              <mat-datepicker
                #picker
                [calendarHeaderComponent]="customCalHeader"
                [startAt]="startCalendarAt()"
              ></mat-datepicker>
            </div>
            <p
              [attr.id]="'date-hint-' + i"
              class="font-lato text-brand-muted mt-1 text-xs"
            >
              Must be at least 30 days from today. Weekends and holidays
              excluded.
            </p>
          </div>
        }
      </div>
    }

    @if (serviceOptions().length > 1) {
      @if (formRef.value.services.length !== serviceOptions().length) {
        <button
          type="button"
          class="comcast-inline-link mt-4"
          (click)="addServicesFormControl()"
        >
          <mat-icon class="comcast-select-icon text-blue-700" aria-hidden="true"
            >add_circle_outline</mat-icon
          >
          {{ addButtonLabel }}
        </button>
      }
    }

    @if (
      selectedServicesTotal() === serviceOptions().length &&
      serviceOptions().length > 0
    ) {
      <p class="font-lato text-brand-muted mt-4 text-sm">
        {{ noMoreServicesMessage }}
      </p>
    }
  </div>
}
